import { Controller } from "@hotwired/stimulus"
import { calculate } from "../modules/salary_lab/calculator_api"
import { formatCurrency, parseNumber } from "../modules/salary_lab/formatter"
import { SmartDebounce, detectEventType } from "../modules/salary_lab/smart_debounce"
import { writeGenericToUrl, readGenericFromUrl } from "../modules/salary_lab/url_state"
import { trackCalculatorUsed } from "../modules/salary_lab/ga4_tracker"

const URL_MAP = { current_salary: "s", current_city: "from", target_city: "to", kiwisaver_perc: "ks", include_student_loan: "sl" }
const URL_TYPES = { current_salary: "number", kiwisaver_perc: "number", include_student_loan: "boolean" }

export default class extends Controller {
  static targets = [
    "salaryInput", "currentCitySelect", "targetCitySelect",
    "kiwisaverSelect", "studentLoanCheckbox",
    "resultsPanel", "loadingIndicator", "introContent",
    "heroLabel", "heroEquivalent", "heroSubtext", "heroDifference",
    "purchasingPowerText", "purchasingPowerPercent", "purchasingPowerLabel",
    "citiesBody",
    "liveRegion"
  ]

  connect() {
    this._debounce = new SmartDebounce(() => this._performCalculation(), { minDigits: 4 })
    this._prefillFromUrl()
    this._renderPrerenderedExtras()
  }

  disconnect() {
    this._debounce.cancel()
  }

  inputChanged(event) {
    this._debounce.schedule(detectEventType(event), this.salaryInputTarget.value)
  }

  optionChanged() {
    this._debounce.schedule("toggle", this.salaryInputTarget.value)
  }

  submitForm(event) {
    event.preventDefault()
    this._debounce.schedule("submit")
  }

  // --- Private ---

  async _performCalculation() {
    const salary = parseNumber(this.salaryInputTarget.value)
    if (!salary || salary <= 0) return

    this._showLoading()

    try {
      const params = {
        type: "cost_of_living",
        current_salary: salary,
        current_city: this.currentCitySelectTarget.value,
        target_city: this.targetCitySelectTarget.value,
        kiwisaver_perc: this.kiwisaverSelectTarget.value,
        include_student_loan: this.studentLoanCheckboxTarget.checked
      }

      const data = await calculate(params)
      this._renderResults(data)
      writeGenericToUrl(params, URL_MAP)
      trackCalculatorUsed("cost_of_living", { salaryAmount: salary })
    } catch (error) {
      if (this.hasLiveRegionTarget) {
        this.liveRegionTarget.textContent = error.message || "Calculation failed. Please try again."
      }
    }
  }

  _renderResults(data) {
    // Hero — equivalent salary with clear directional wording
    this.heroEquivalentTarget.textContent = formatCurrency(data.equivalent_salary)

    if (this.hasHeroLabelTarget) {
      this.heroLabelTarget.textContent = `Equivalent salary in ${data.target_city}`
    }

    const diff = data.salary_difference // equivalent - current (negative = cheaper target)
    const absDiff = Math.abs(diff)

    if (diff < 0) {
      this.heroSubtextTarget.textContent =
        `To match your ${formatCurrency(data.current_salary)} ${data.current_city} lifestyle, you'd only need ${formatCurrency(data.equivalent_salary)} in ${data.target_city}`
      this.heroDifferenceTarget.textContent = `${formatCurrency(absDiff)} less per year`
      this._setDiffColor(this.heroDifferenceTarget, "green")
    } else if (diff > 0) {
      this.heroSubtextTarget.textContent =
        `To match your ${formatCurrency(data.current_salary)} ${data.current_city} lifestyle, you'd need ${formatCurrency(data.equivalent_salary)} in ${data.target_city}`
      this.heroDifferenceTarget.textContent = `${formatCurrency(absDiff)} more per year`
      this._setDiffColor(this.heroDifferenceTarget, "red")
    } else {
      this.heroSubtextTarget.textContent =
        `${data.current_city} and ${data.target_city} have similar costs of living`
      this.heroDifferenceTarget.textContent = "Same cost of living"
      this._setDiffColor(this.heroDifferenceTarget, "gray")
    }

    // Purchasing power insight
    this._renderPurchasingPower(data)

    // All cities table
    if (data.all_cities) {
      this.citiesBodyTarget.innerHTML = data.all_cities.map(c => {
        const isCurrent = c.city === data.current_city
        const isTarget = c.city === data.target_city
        const rowClass = isCurrent ? "bg-cyan-50" : isTarget ? "bg-amber-50" : ""
        const badge = isCurrent ? " <span class=\"text-xs text-cyan-600\">(you)</span>" : isTarget ? " <span class=\"text-xs text-amber-600\">(target)</span>" : ""
        return `<tr class="${rowClass}">
          <td class="px-4 py-2.5 text-gray-700">${c.city}${badge}</td>
          <td class="px-4 py-2.5 text-right tabular-nums text-gray-500">${c.index}</td>
          <td class="px-4 py-2.5 text-right tabular-nums font-medium text-gray-900">${formatCurrency(c.equivalent_salary)}</td>
          <td class="px-4 py-2.5 text-right tabular-nums text-cyan-700">${formatCurrency(c.take_home)}</td>
        </tr>`
      }).join("")
    }

    this._showResults()

    // ARIA
    if (this.hasLiveRegionTarget) {
      const direction = diff < 0 ? "less" : diff > 0 ? "more" : "the same"
      this.liveRegionTarget.textContent =
        `Equivalent salary in ${data.target_city}: ${formatCurrency(data.equivalent_salary)}. ` +
        `That's ${formatCurrency(absDiff)} ${direction} per year.`
    }
  }

  _renderPrerenderedExtras() {
    if (this.element.dataset.prerendered !== "true") return
    const json = this.element.dataset.prerenderedResults
    if (!json) return
    try {
      const data = JSON.parse(json)
      this._renderPurchasingPower(data)
      if (data.all_cities) {
        this.citiesBodyTarget.innerHTML = data.all_cities.map(c => {
          const isCurrent = c.city === data.current_city
          const isTarget = c.city === data.target_city
          const rowClass = isCurrent ? "bg-cyan-50" : isTarget ? "bg-amber-50" : ""
          const badge = isCurrent ? " <span class=\"text-xs text-cyan-600\">(you)</span>" : isTarget ? " <span class=\"text-xs text-amber-600\">(target)</span>" : ""
          return `<tr class="${rowClass}">
            <td class="px-4 py-2.5 text-gray-700">${c.city}${badge}</td>
            <td class="px-4 py-2.5 text-right tabular-nums text-gray-500">${c.index}</td>
            <td class="px-4 py-2.5 text-right tabular-nums font-medium text-gray-900">${formatCurrency(c.equivalent_salary)}</td>
            <td class="px-4 py-2.5 text-right tabular-nums text-cyan-700">${formatCurrency(c.take_home)}</td>
          </tr>`
        }).join("")
      }
      const diff = data.salary_difference
      if (this.hasHeroDifferenceTarget) {
        this._setDiffColor(this.heroDifferenceTarget, diff < 0 ? "green" : diff > 0 ? "red" : "gray")
      }
    } catch { /* ignore parse errors */ }
  }

  _showLoading() {
    if (this.hasLoadingIndicatorTarget) this.loadingIndicatorTarget.classList.remove("hidden")
    if (this.hasResultsPanelTarget) this.resultsPanelTarget.classList.add("hidden")
    if (this.hasIntroContentTarget) this.introContentTarget.classList.add("hidden")
  }

  _showResults() {
    if (this.hasLoadingIndicatorTarget) this.loadingIndicatorTarget.classList.add("hidden")
    if (this.hasResultsPanelTarget) this.resultsPanelTarget.classList.remove("hidden")
    if (this.hasIntroContentTarget) this.introContentTarget.classList.add("hidden")
  }

  _renderPurchasingPower(data) {
    if (!this.hasPurchasingPowerTextTarget) return

    const powerChange = ((data.current_salary / data.equivalent_salary) - 1) * 100
    const absPower = Math.abs(powerChange).toFixed(0)

    if (powerChange > 0) {
      this.purchasingPowerTextTarget.textContent =
        `If you keep earning ${formatCurrency(data.current_salary)} in ${data.target_city}, your money goes further than in ${data.current_city}.`
      this.purchasingPowerPercentTarget.textContent = `${absPower}%`
      this._setDiffColor(this.purchasingPowerPercentTarget, "green")
      this.purchasingPowerLabelTarget.textContent = "more purchasing power"
    } else if (powerChange < 0) {
      this.purchasingPowerTextTarget.textContent =
        `If you keep earning ${formatCurrency(data.current_salary)} in ${data.target_city}, your money won't stretch as far as in ${data.current_city}.`
      this.purchasingPowerPercentTarget.textContent = `${absPower}%`
      this._setDiffColor(this.purchasingPowerPercentTarget, "red")
      this.purchasingPowerLabelTarget.textContent = "less purchasing power"
    } else {
      this.purchasingPowerTextTarget.textContent =
        `Your money has the same purchasing power in both cities.`
      this.purchasingPowerPercentTarget.textContent = "0%"
      this._setDiffColor(this.purchasingPowerPercentTarget, "gray")
      this.purchasingPowerLabelTarget.textContent = ""
    }
  }

  _prefillFromUrl() {
    const p = readGenericFromUrl(URL_MAP, URL_TYPES)
    if (!p.current_salary) return
    this.salaryInputTarget.value = p.current_salary
    if (p.current_city) this.currentCitySelectTarget.value = p.current_city
    if (p.target_city) this.targetCitySelectTarget.value = p.target_city
    if (p.kiwisaver_perc != null) this.kiwisaverSelectTarget.value = p.kiwisaver_perc
    if (p.include_student_loan) this.studentLoanCheckboxTarget.checked = true
    if (this.element.dataset.prerendered !== "true") this._performCalculation()
  }

  _setDiffColor(el, color) {
    el.classList.remove("text-green-600", "text-red-600", "text-gray-600")
    el.classList.add(`text-${color}-600`)
  }
}
